import { useEffect } from 'react';
import { SecurityCheckStatus } from 'types/common';

type WithSecurityCheckStatus = {
    securityChecksStatus?: SecurityCheckStatus;
};

interface Options {
    enabled?: boolean;
    intervalMs?: number;
}

const DEFAULT_POLL_INTERVAL_MS = 10_000;

// Polls `refetch` on an interval while any item in `list` hasn't finished its security check yet,
// throttled by `lastFetchedAt` so it doesn't refetch more often than `intervalMs`.
const usePollPendingSecurityChecks = <T extends WithSecurityCheckStatus>(
    list: T[] | undefined,
    refetch: () => void,
    lastFetchedAt: number | null,
    { enabled = true, intervalMs = DEFAULT_POLL_INTERVAL_MS }: Options = {}
) => {
    useEffect(() => {
        if (!enabled) return;

        const intervalId = setInterval(() => {
            const hasPendingItems = list?.some(
                (item) => item.securityChecksStatus && item.securityChecksStatus !== 'VERIFIED'
            );

            if (!hasPendingItems) return;

            const now = Date.now();
            const isStale = !lastFetchedAt || now - lastFetchedAt >= intervalMs;

            if (isStale) {
                refetch();
            }
        }, intervalMs);

        return () => clearInterval(intervalId);
    }, [list, refetch, lastFetchedAt, enabled, intervalMs]);
};

export default usePollPendingSecurityChecks;
